import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { useWhatsNew } from '@/hooks/useWhatsNew';

const LATEST_HIGHLIGHTS = [
   'update_ai_companion',
   'update_reflection_journal',
   'update_15_new_rooms',
   'update_recently_visited',
];

export const UpdateToast = () => {
  const { t } = useLanguage();
  const { hasNewUpdates, currentVersion, markAsSeen } = useWhatsNew();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Show toast after a short delay if there are new updates
    if (hasNewUpdates && !isDismissed) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [hasNewUpdates, isDismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
  };

  const handleViewUpdates = () => {
    markAsSeen();
    setIsVisible(false);
    setIsDismissed(true);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className="fixed bottom-6 right-6 z-50 max-w-sm"
        >
          <div className="bg-card/95 backdrop-blur-md border border-primary/20 rounded-lg shadow-2xl shadow-primary/5 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-primary/5 border-b border-primary/10">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">
                  {t('new') || 'NEW'} v{currentVersion}
                </span>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1 text-muted-foreground/50 hover:text-foreground transition-colors rounded"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="px-4 py-3 space-y-3">
              <p className="text-xs text-muted-foreground">
                {t('whats_new_intro') || 'Latest updates:'}
              </p>
              <ul className="space-y-1.5">
                {LATEST_HIGHLIGHTS.map((key, index) => (
                  <motion.li
                    key={key}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.1 }}
                    className="flex items-start gap-2 text-sm text-foreground/80"
                  >
                    <span className="text-primary mt-0.5">•</span>
                    <span>{t(key)}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-border/20">
              <Link
                to="/updates"
                onClick={handleViewUpdates}
                className="flex items-center justify-center gap-2 w-full py-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors group"
              >
                {t('view_all_updates') || 'View all updates'}
                <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UpdateToast;
